import type { Order, User } from "../../api"

type UserStats = {
  total: number
  verifiedPurchased: number
  verifiedNotPurchased: number
  unverifiedPurchased: number
  unverifiedNotPurchased: number
}

type DailySignupEntry = {
  date: string
  total: number
  verified: number
}

type OrderStatusEntry = {
  status: string
  count: number
}

type MonthlyRevenueEntry = {
  month: string
  revenue: number
  count: number
}

type PackageEntry = {
  name: string
  count: number
}

type CountryEntry = {
  country: string
  count: number
}

type ConversionRates = {
  signupToVerified: number
  verifiedToPurchased: number
  signupToPurchased: number
}

const formatMonthLabel = (date: Date): string => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  return `${year}-${month}`
}

const formatDayLabel = (date: Date): string => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export const groupUsersByDay = (users: User[]): DailySignupEntry[] => {
  const buckets = new Map<string, { total: number; verified: number }>()

  for (const user of users) {
    const key = formatDayLabel(new Date(user.createdAt))
    const entry = buckets.get(key) ?? { total: 0, verified: 0 }
    entry.total += 1
    if (user.isVerified) entry.verified += 1
    buckets.set(key, entry)
  }

  const sorted = Array.from(buckets.entries()).sort(([a], [b]) => a.localeCompare(b))

  let cumulativeTotal = 0
  let cumulativeVerified = 0
  return sorted.map(([date, counts]) => {
    cumulativeTotal += counts.total
    cumulativeVerified += counts.verified
    return { date, total: cumulativeTotal, verified: cumulativeVerified }
  })
}

export const toCumulativeSignups = (
  dailySignups: ReadonlyArray<{ date: string; total: number; verified: number }>,
): DailySignupEntry[] => {
  let cumulativeTotal = 0
  let cumulativeVerified = 0
  return dailySignups.map(({ date, total, verified }) => {
    cumulativeTotal += total
    cumulativeVerified += verified
    return { date, total: cumulativeTotal, verified: cumulativeVerified }
  })
}

export const groupOrdersByStatus = (orders: Order[]): OrderStatusEntry[] => {
  const counts = new Map<string, number>()

  for (const order of orders) {
    const status = order.status
    counts.set(status, (counts.get(status) ?? 0) + 1)
  }

  const statusOrder = ['purchased', 'created', 'received', 'in_progress', 'completed', 'failed', 'cancelled', 'unknown', 'deleted']

  return statusOrder
    .filter(s => counts.has(s))
    .map(status => ({ status, count: counts.get(status)! }))
}

export const computeMonthlyRevenue = (orders: Order[]): MonthlyRevenueEntry[] => {
  const paidOrders = orders.filter(o => o.price !== null)
  const buckets = new Map<string, { revenue: number; count: number }>()

  for (const order of paidOrders) {
    const key = formatMonthLabel(new Date(order.createdAt))
    const entry = buckets.get(key) ?? { revenue: 0, count: 0 }
    entry.revenue += order.price!
    entry.count += 1
    buckets.set(key, entry)
  }

  return Array.from(buckets.entries())
    .map(([month, data]) => ({ month, ...data }))
    .sort((a, b) => a.month.localeCompare(b.month))
}

export const groupOrdersByPackage = (
  orders: Order[],
  packageMap: Record<string, { name: string }>,
): PackageEntry[] => {
  const counts = new Map<string, number>()

  for (const order of orders) {
    const pkgId = order.packageId ?? 'unknown'
    const name = packageMap[pkgId]?.name ?? pkgId
    counts.set(name, (counts.get(name) ?? 0) + 1)
  }

  return Array.from(counts.entries())
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count)
}

export const groupUsersByCountry = (users: User[]): CountryEntry[] => {
  const counts = new Map<string, number>()

  for (const user of users) {
    const country = user.countryCode || 'Unknown'
    counts.set(country, (counts.get(country) ?? 0) + 1)
  }

  const sorted = Array.from(counts.entries())
    .map(([country, count]) => ({ country, count }))
    .sort((a, b) => b.count - a.count)

  if (sorted.length <= 10) return sorted

  const top10 = sorted.slice(0, 10)
  const otherCount = sorted.slice(10).reduce((sum, e) => sum + e.count, 0)
  return [...top10, { country: 'Other', count: otherCount }]
}

export const computeConversionRates = (stats: UserStats): ConversionRates => {
  const verified = stats.verifiedPurchased + stats.verifiedNotPurchased
  const totalPurchased = stats.verifiedPurchased + stats.unverifiedPurchased

  return {
    signupToVerified: stats.total > 0 ? verified / stats.total : 0,
    verifiedToPurchased: verified > 0 ? stats.verifiedPurchased / verified : 0,
    signupToPurchased: stats.total > 0 ? totalPurchased / stats.total : 0,
  }
}

export type {
  UserStats,
  DailySignupEntry,
  OrderStatusEntry,
  MonthlyRevenueEntry,
  PackageEntry,
  CountryEntry,
  ConversionRates,
}
